import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError  } from 'rxjs';
import { environment } from 'src/environments/environment';
import { retryWhen, delay, take, catchError, tap } from 'rxjs/operators';
import { SnackbarService } from 'src/app/services/snackbar/snackbar.service';
import { LoginService } from 'src/app/services/login/login.service';
import{ FseIo } from 'src/app/utils/interfaces/new/FseIo.interface';
import { TokenjFSE } from 'src/app/utils/interfaces/new/TokenjFSE';

export interface ReaderStatus {
  id: string;
  version: string;
  pid: string;
  architecture: string;
  javaRuntime: string;
  javaVersion: string;
  operatingSystem: string;
  protocol: string;
  serverVersion: string;
  socle: string;
  status: number;
  statusDescription: string;
}

export interface ListeDICO {
  code?: string;
  libelle?: string;
  typeDest?: string;
}

export interface ListeSituation {
  code?:string; // Code de la situation
  libelle?:string; // Libellé de la situation
  tauxStd?:string; // Taux standard de la situation
  tauxAld?:string; // Taux ALD de la situation
}

export interface LigneOrganisme {
  adresse1?: string;
  adresse2?: string;
  cedex?: string;
  codeCaisse: string;
  codeCentre: string;
  codeCentreInfo?: string;
  codeDestinataire?: string;
  codePostal?: string;
  codeRegime?: string;
  commune?: string;
  fax?: string;
  nomCaisse?: string;
  nomRegime?: string;
  telephone?: string;
}
@Injectable({
  providedIn: 'root',
})

export class JfseService {

  private baseUrl = 'https://resip-fse-hg-test.cegedim.com/jfse-proxy'; // Replace with {{EndpointJFSEserveur}}
  private maxRetries = 3; // Nombre maximal de tentatives
  private retryDelay = 2000; // Délai entre chaque tentative (en millisecondes)
  private readersessionKey = 'jFSEreaderStatus';
  private JFSEsessionKey = 'iDjFSE';
  private currentUser: any;
  private Liste_regime: ListeDICO[]=[];
  private Liste_qualite: ListeDICO[]=[];
  private Liste_codeGestion: ListeDICO[]=[];
  private Liste_Situation: ListeSituation[]=[];
  private Liste_SituationRegime: string;
  private Liste_Organismes: LigneOrganisme[]=[];
  private Liste_OrganismesRegime: string;

  private httpOptions = {
      withCredentials: true,
      observe: 'response' as 'response',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: undefined
  };

  constructor(
    private http: HttpClient,
    private _SnackbarService: SnackbarService,
    private _login: LoginService,
  ) {}

  private getHeaders(token: string): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  public async getReaderStatusWithRetry(maxRetries: number = 3, delayMs: number = 2000): Promise<ReaderStatus | null> {
    this.currentUser = this._login.getCurrentProfil();
    const readerLocalUri = `${environment.jFSEreaderLocalsrv}/id`;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        if(attempt > 2){
          console.log(`Tentative ${attempt}/${maxRetries}...`);
          this._SnackbarService.openSnackBar(`Erreur détectée, connection lecteur impossible nouvelle tentative ${attempt}/${maxRetries} dans quelques secondes... `);
        }
        const response = await this.http.get<ReaderStatus>(readerLocalUri).toPromise();
        // const response = simulatedData;
        if (response && response.id && response.statusDescription) {
          return response; // Retourne les données valides si tout fonctionne
        } else {
          console.warn('Données invalides reçues:', response);
          return null; // Retourne null si les données ne sont pas valides
        }
      } catch (error) {
        console.error(`Erreur lors de la tentative ${attempt}:`, error.message);

        if (attempt < maxRetries) {
          // Attend avant la prochaine tentative
          await this.delay(delayMs);
        } else {
          console.error('Échec après plusieurs tentatives');
          throw new Error('Impossible de joindre le service après plusieurs tentatives.');
        }
      }
    }

    return null; // Au cas où toutes les tentatives échoueraient
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Enregistrer dans sessionStorage
  public saveToSession(userId:string, key:string, value:string): void {
    const userKey = `${key}_${userId}`;
    sessionStorage.setItem(userKey, value);
  }
  public getFromSession(userId:string, key:string): string {
    const userKey = `${key}_${userId}`;
    const data = sessionStorage.getItem(userKey);
    return data ? data : '';
  }
  public removeFromSession(userId: string, key:string): void {
    const userKey = `${key}_${userId}`;
    sessionStorage.removeItem(userKey);
  }

  // Enregistrer dans sessionStorage
  public saveReaderStatusToSession(userId: string, readerStatus: ReaderStatus): void {
    this.currentUser = this._login.getCurrentProfil();
    const userKey = `${this.readersessionKey}_${userId}`;
    sessionStorage.setItem(userKey, JSON.stringify(readerStatus));
  }
  // Récupérer depuis sessionStorage
  public getReaderStatusFromSession(userId: string): ReaderStatus | null {
    this.currentUser = this._login.getCurrentProfil();
    const userKey = `${this.readersessionKey}_${userId}`;
    const data = sessionStorage.getItem(userKey);
    // if(data){// Temporaire pour les tests CNDA avec le serveur de développement JFSE
    //   const readerStatus: ReaderStatus = JSON.parse(data);
    //   readerStatus.operatingSystem = 'WINDOWS';
    //   return readerStatus ? readerStatus : null;
    // }

    return data ? JSON.parse(data) : null;
  }
  // Supprimer depuis sessionStorage
  public removeReaderStatusFromSession(userId: string): void {
    const userKey = `${this.readersessionKey}_${userId}`;
    sessionStorage.removeItem(userKey);
  }

  public initTabGuiFse(idJfse:number): Promise<FseIo> {
    const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
    const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
    if(readerStatus){ // si pas de reader erreur snack bar
      const body = {
        idJfse: idJfse, // Obligatoire 0 si inconnu
        tokenIdJfse:token,
        cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        OS:readerStatus.operatingSystem, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
      };

       return new Promise<FseIo>(async (resolve, reject) => {
         try {
           const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/tabGestFSE`, body, this.httpOptions).toPromise();
           if(res.body?.newToken){
            this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
           }
           resolve(res.body);
         } catch (e) {
           if (e.error && e.error.message) {
             this._SnackbarService.openSnackBar(e.error.message);
           }
           reject(e);
         }
         });
    }else{
      this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
    }
  }

  public initTabGuiScor(idJfse:number): Promise<FseIo>{
    const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
    const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
    if(readerStatus){ // si pas de reader erreur snack bar
      const body = {
        idJfse: idJfse, // Obligatoire 0 si inconnu
        tokenIdJfse:token,
        cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        OS:readerStatus.operatingSystem, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
      };

       return new Promise<FseIo>(async (resolve, reject) => {
         try {
           const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/tabGestSCOR`, body, this.httpOptions).toPromise();
           if(res.body?.newToken){
            this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
           }
           resolve(res.body);
         } catch (e) {
           if (e.error && e.error.message) {
             this._SnackbarService.openSnackBar(e.error.message);
           }
           reject(e);
         }
         });
    }else{
      this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
    }
  }

  public initTabGuiNOE(idJfse:number): Promise<FseIo>{
    const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
    const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
    if(readerStatus){ // si pas de reader erreur snack bar
      const body = {
        idJfse: idJfse, // Obligatoire 0 si inconnu
        tokenIdJfse:token,
        cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        OS:readerStatus.operatingSystem, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
      };

       return new Promise<FseIo>(async (resolve, reject) => {
         try {
           const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/tabGestNOE`, body, this.httpOptions).toPromise();
           if(res.body?.newToken){
            this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
           }
           resolve(res.body);
         } catch (e) {
           if (e.error && e.error.message) {
             this._SnackbarService.openSnackBar(e.error.message);
           }
           reject(e);
         }
         });
    }else{
      this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
    }
  }


  public initTabGuiUserParam(idJfse:number): Promise<FseIo>{
    const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
    const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
    if(readerStatus){ // si pas de reader erreur snack bar
      const body = {
        idJfse: idJfse, // Obligatoire 0 si inconnu
        tokenIdJfse:token,
        cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        OS:readerStatus.operatingSystem, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
      };

       return new Promise<FseIo>(async (resolve, reject) => {
         try {
           const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/UserParameters`, body, this.httpOptions).toPromise();
           if(res.body?.newToken){
            this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
           }
           resolve(res.body);
         } catch (e) {
           if (e.error && e.error.message) {
             this._SnackbarService.openSnackBar(e.error.message);
           }
           reject(e);
         }
         });
    }else{
      this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
    }
  }

  public initFSE(idJfse:number, beneficiaire:any, contextfacture:any, organismeComplementaire:any = undefined): Promise<FseIo>{
    const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
    const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
    if(readerStatus){ // si pas de reader erreur snack bar
      const body = {
        idJfse: idJfse, // Obligatoire 0 si inconnu
        tokenIdJfse:token,
        cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        OS:readerStatus.operatingSystem, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        beneficiaire:beneficiaire,
        facture:contextfacture,
        organismeComplementaire:organismeComplementaire ? organismeComplementaire : undefined,
      };

       return new Promise<FseIo>(async (resolve, reject) => {
         try {
           const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/initFSE`, body, this.httpOptions).toPromise();
           if(res.body?.newToken){
            this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
           }
           resolve(res.body);
         } catch (e) {
           if (e.error && e.error.message) {
             this._SnackbarService.openSnackBar(e.error.message);
           }
           reject(e);
         }
         });
    }else{
      this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
    }
  }

  public initTabTLT(idJfse:number): Promise<FseIo>{
    const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
    const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
    if(readerStatus){ // si pas de reader erreur snack bar
      const body = {
        idJfse: idJfse, // Obligatoire 0 si inconnu
        tokenIdJfse:token,
        cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        OS:readerStatus.operatingSystem, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
      };

       return new Promise<FseIo>(async (resolve, reject) => {
         try {
           const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/teletransmission`, body, this.httpOptions).toPromise();
           if(res.body?.newToken){
            this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
           }
           resolve(res.body);
         } catch (e) {
           if (e.error && e.error.message) {
             this._SnackbarService.openSnackBar(e.error.message);
           }
           reject(e);
         }
         });
    }else{
      this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
    }
  }

  public async lireCPS(): Promise<FseIo> {
    // const rawIdJfse = this.getFromSession(this.currentUser.id, 'iDjfse');
    // const idJfse = isNaN(Number(rawIdJfse)) ? 0 : Number(rawIdJfse); //securité si JFSE non numerique
    const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
    if(readerStatus){ // si pas de reader erreur snack bar
      const body = {
        idJfse: 0, // Obligatoire 0 si inconnu
        tokenIdJfse: "", // Optionnel voir si present dans sessionStorage
        // codePorteur: "", // Optionnel 4 chiffre
        cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        OS:readerStatus.operatingSystem, //+' '+readerStatus.architecture - recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
      };

      return new Promise<FseIo>(async (resolve, reject) => {
        try {
          const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/lireCPS`, body, this.httpOptions).toPromise();
          resolve(res.body);
        } catch (e) {
          if (e.error && e.error.message) {
            this._SnackbarService.openSnackBar(e.error.message);
          }
          reject(e);
        }
        });
        // Retourne uniquement le corps de la réponse
    }else{
      this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
    }

  }

  public async getUserToken(idJfse:number,force: boolean = false): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
      try {
        let token = null
        if(!force){
          token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
        }
        if(!token){
          const body = {
            idJfse: idJfse, // Obligatoire 0 si inconnu
          }
          const res = await this.http.post<TokenjFSE>(`${environment.basePath}/fse/praticien/getUserToken`, body, this.httpOptions).toPromise();
          if(res.body){
            if(res.body.error){
              this._SnackbarService.openSnackBar(res.body.errorMessage);
              reject(res.body.errorMessage);
            }else if(res.body.token){
              this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.token);
              resolve(res.body.token);
            }
          }
        }
        resolve(token);
      } catch (e) {
        if (e.error && e.error.message) {
          this._SnackbarService.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  public async getAdminToken(): Promise<string> { //uniquement depuis admin HMO
    return new Promise<string>(async (resolve, reject) => {
      try {
        const token = "";
        if(!token){
          const body = {
          }
          const res = await this.http.post<TokenjFSE>(`${environment.basePath}/fse/praticien/getAdminToken`, body, this.httpOptions).toPromise();
          if(res.body){
            if(res.body.error){
              this._SnackbarService.openSnackBar(res.body.errorMessage);
              reject(res.body.errorMessage);
            }else if(res.body.token){
              // this.saveToSession(this.currentUser.id, 'jFseAdminToken', res.body.token);
              resolve(res.body.token);
            }
          }
        }
        resolve(token);
      } catch (e) {
        if (e.error && e.error.message) {
          this._SnackbarService.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  public async lireCV(): Promise<FseIo> {
    const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
    // const token = "qsdfghjklmkjhgfd";
    const rawIdJfse = this.getFromSession(this.currentUser.id, 'iDjfse');
    const idJfse = isNaN(Number(rawIdJfse)) ? 0 : Number(rawIdJfse); //securité si JFSE non numerique
    const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
    if(readerStatus){ // si pas de reader erreur snack bar
      const body = {
        idJfse: idJfse, // Obligatoire 0 si inconnu
        // tokenIdJfse: token, // Optionnel voir si present dans sessionStorage
        tokenIdJfse:token,
        // codePorteur: "", // Optionnel 4 chiffre
        cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        OS:readerStatus.operatingSystem, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
      };

       return new Promise<FseIo>(async (resolve, reject) => {
         try {
           const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/lireCV`, body, this.httpOptions).toPromise();
           if(res.body?.newToken){
            this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
           }
           resolve(res.body);
         } catch (e) {
           if (e.error && e.error.message) {
             this._SnackbarService.openSnackBar(e.error.message);
           }
           reject(e);
         }
         });
    }else{
      this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
    }
  }

  public async getBenefViaApCV(mode:number, contextApCV?:string | ''): Promise<FseIo> {
    const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
    // const token = "qsdfghjklmkjhgfd";
    const rawIdJfse = this.getFromSession(this.currentUser.id, 'iDjfse');
    const idJfse = isNaN(Number(rawIdJfse)) ? 0 : Number(rawIdJfse); //securité si JFSE non numerique
    const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
    if(readerStatus){ // si pas de reader erreur snack bar
      const body = {
        idJfse: idJfse, // Obligatoire 0 si inconnu
        // tokenIdJfse: token, // Optionnel voir si present dans sessionStorage
        tokenIdJfse:token,
        // codePorteur: "", // Optionnel 4 chiffre
        cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        OS:readerStatus.operatingSystem, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        mode: mode,
        contextApCV: contextApCV,
      };

       return new Promise<FseIo>(async (resolve, reject) => {
         try {
           const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/getBenefViaApCV`, body, this.httpOptions).toPromise();
           if(res.body?.newToken){
            this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
           }
           resolve(res.body);
         } catch (e) {
           if (e.error && e.error.message) {
             this._SnackbarService.openSnackBar(e.error.message);
           }
           reject(e);
         }
         });
    }else{
      this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
    }
  }


  public calculerCleNIR(nir: string): number | null {
    if (!/^[12]\d{2}\d{2}([0-9]{2}|2A|2B)\d{6}$/.test(nir)) {
      console.error("Format du NIR invalide (doit faire 13 caractères avant clé)");
      return null;
    }

    // Cas spécifique Corse (2A ou 2B)
    let nirNumerique = nir.toUpperCase().replace('A', '0').replace('B', '0');

    let correction = 0;
    if (nir.includes('A')) correction = 1000000;
    if (nir.includes('B')) correction = 2000000;

    const nirNumber = parseInt(nirNumerique, 10) - correction;

    const cle = 97 - (nirNumber % 97);
    return cle;
  }

  public async getListeRegimes(force:boolean = false): Promise<ListeDICO[]> {
      if(this.Liste_regime.length == 0 || force){
        const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
        const rawIdJfse = this.getFromSession(this.currentUser.id, 'iDjfse');
        const idJfse = isNaN(Number(rawIdJfse)) ? 0 : Number(rawIdJfse); //securité si JFSE non numerique
        const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
        if(readerStatus){ // si pas de reader erreur snack bar
          const body = {
            idJfse: idJfse, // Obligatoire 0 si inconnu
            // tokenIdJfse: token, // Optionnel voir si present dans sessionStorage
            tokenIdJfse:token,
            // codePorteur: "", // Optionnel 4 chiffre
            cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
            OS:readerStatus.operatingSystem, //+ readerStatus.architecture recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
          };

           return new Promise<ListeDICO[]>(async (resolve, reject) => {
             try {
               const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/getListeRegimes`, body, this.httpOptions).toPromise();
               if(res.body?.newToken){
                this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
               }

               // Vérification de la présence des données attendues
                const lst = res.body?.method?.output?.lst;

                if (!Array.isArray(lst) || lst.length === 0) {
                    this._SnackbarService.openSnackBar("Aucune donnée de régime reçue.");
                    return reject(new Error("ListeRegime est vide ou non définie."));
                }

                // Mise à jour et retour des données
                this.Liste_regime = lst;
                resolve(this.Liste_regime);
             } catch (e) {
               if (e.error && e.error.message) {
                 this._SnackbarService.openSnackBar(e.error.message);
               }
               reject(e);
             }
             });
        }else{
          this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
        }
      }else{ // on a deja fait appel a getListRegime rappel non necessaire.
        return this.Liste_regime;
      }
  }

  public async getListeQualites(force:boolean = false): Promise<ListeDICO[]> {
    if(this.Liste_qualite.length == 0 || force){
      const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
      const rawIdJfse = this.getFromSession(this.currentUser.id, 'iDjfse');
      const idJfse = isNaN(Number(rawIdJfse)) ? 0 : Number(rawIdJfse); //securité si JFSE non numerique
      const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
      if(readerStatus){ // si pas de reader erreur snack bar
        const body = {
          idJfse: idJfse, // Obligatoire 0 si inconnu
          // tokenIdJfse: token, // Optionnel voir si present dans sessionStorage
          tokenIdJfse:token,
          // codePorteur: "", // Optionnel 4 chiffre
          cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
          OS:readerStatus.operatingSystem, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        };

         return new Promise<ListeDICO[]>(async (resolve, reject) => {
           try {
             const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/getListeQualites`, body, this.httpOptions).toPromise();
             if(res.body?.newToken){
              this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
             }

             // Vérification de la présence des données attendues
              const lst = res.body?.method?.output?.lst;

              if (!Array.isArray(lst) || lst.length === 0) {
                  this._SnackbarService.openSnackBar("Aucune donnée de régime reçue.");
                  return reject(new Error("ListeRegime est vide ou non définie."));
              }

              // Mise à jour et retour des données
              this.Liste_qualite = lst;
              resolve(this.Liste_qualite);
           } catch (e) {
             if (e.error && e.error.message) {
               this._SnackbarService.openSnackBar(e.error.message);
             }
             reject(e);
           }
           });
      }else{
        this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
      }
    }else{ // on a deja fait appel a getListRegime rappel non necessaire.
      return this.Liste_qualite;
    }
  }

  public async getListeCodeGestions(force:boolean = false): Promise<ListeDICO[]> {
    if(this.Liste_codeGestion.length == 0 || force){
      const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
      const rawIdJfse = this.getFromSession(this.currentUser.id, 'iDjfse');
      const idJfse = isNaN(Number(rawIdJfse)) ? 0 : Number(rawIdJfse); //securité si JFSE non numerique
      const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
      if(readerStatus){ // si pas de reader erreur snack bar
        const body = {
          idJfse: idJfse, // Obligatoire 0 si inconnu
          // tokenIdJfse: token, // Optionnel voir si present dans sessionStorage
          tokenIdJfse:token,
          // codePorteur: "", // Optionnel 4 chiffre
          cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
          OS:readerStatus.operatingSystem, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        };

        return new Promise<ListeDICO[]>(async (resolve, reject) => {
          try {
            const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/getListeCodeGestions`, body, this.httpOptions).toPromise();
            if(res.body?.newToken){
              this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
            }

            // Vérification de la présence des données attendues
              const lst = res.body?.method?.output?.lst;

              if (!Array.isArray(lst) || lst.length === 0) {
                  this._SnackbarService.openSnackBar("Aucune donnée de régime reçue.");
                  return reject(new Error("ListeRegime est vide ou non définie."));
              }

              // Mise à jour et retour des données
              this.Liste_codeGestion = lst;
              resolve(this.Liste_codeGestion);
          } catch (e) {
            if (e.error && e.error.message) {
              this._SnackbarService.openSnackBar(e.error.message);
            }
            reject(e);
          }
          });
      }else{
        this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
        return Promise.reject(new Error("Aucun lecteur connecté."));
      }
    }else{ // on a deja fait appel a getListRegime rappel non necessaire.
      return this.Liste_codeGestion;
    }
  }

  public async getListeSituations(regime:string, force:boolean = false): Promise<ListeSituation[]> {
    if(this.Liste_Situation.length == 0 || force || this.Liste_SituationRegime !== regime){
      this.Liste_SituationRegime = regime;
      const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
      const rawIdJfse = this.getFromSession(this.currentUser.id, 'iDjfse');
      const idJfse = isNaN(Number(rawIdJfse)) ? 0 : Number(rawIdJfse); //securité si JFSE non numerique
      const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
      if(readerStatus){ // si pas de reader erreur snack bar
        const body = {
          idJfse: idJfse, // Obligatoire 0 si inconnu
          // tokenIdJfse: token, // Optionnel voir si present dans sessionStorage
          tokenIdJfse:token,
          // codePorteur: "", // Optionnel 4 chiffre
          cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
          OS:readerStatus.operatingSystem, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        };

        return new Promise<ListeSituation[]>(async (resolve, reject) => {
          try {
            const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/getListeSituations/${regime}`, body, this.httpOptions).toPromise();
            if(res.body?.newToken){
              this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
            }

            // Vérification de la présence des données attendues
              const lst = res.body?.method?.output?.lst;

              if (!Array.isArray(lst) || lst.length === 0) {
                  this._SnackbarService.openSnackBar("Aucune donnée de régime reçue.");
                  return reject(new Error("ListeRegime est vide ou non définie."));
              }

              // Mise à jour et retour des données
              this.Liste_Situation = lst;
              resolve(this.Liste_Situation);
          } catch (e) {
            if (e.error && e.error.message) {
              this._SnackbarService.openSnackBar(e.error.message);
            }
            reject(e);
          }
          });
      }else{
        this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
        return Promise.reject(new Error("Aucun lecteur connecté."));
      }
    }else{ // on a deja fait appel a getListRegime rappel non necessaire.
      return this.Liste_Situation;
    }
  }

  public async getListeOrganismes(regime:string, force:boolean = false): Promise<LigneOrganisme[]> {
    if(this.Liste_Organismes.length == 0 || force || this.Liste_OrganismesRegime !== regime){
      this.Liste_OrganismesRegime = regime;
      const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
      const rawIdJfse = this.getFromSession(this.currentUser.id, 'iDjfse');
      const idJfse = isNaN(Number(rawIdJfse)) ? 0 : Number(rawIdJfse); //securité si JFSE non numerique
      const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
      if(readerStatus){ // si pas de reader erreur snack bar
        const body = {
          idJfse: idJfse, // Obligatoire 0 si inconnu
          // tokenIdJfse: token, // Optionnel voir si present dans sessionStorage
          tokenIdJfse:token,
          // codePorteur: "", // Optionnel 4 chiffre
          cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
          OS:readerStatus.operatingSystem, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        };

        return new Promise<LigneOrganisme[]>(async (resolve, reject) => {
          try {
            const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/getListeOrganismes/${regime}`, body, this.httpOptions).toPromise();
            if(res.body?.newToken){
              this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
            }

            // Vérification de la présence des données attendues
              const lst = res.body?.method?.output?.lstOrganismes;

              if (!Array.isArray(lst) || lst.length === 0) {
                  this._SnackbarService.openSnackBar("Aucune donnée d'organismes reçue.");
                  return reject(new Error("ListeOrganismes est vide ou non définie."));
              }

              // Mise à jour et retour des données
              this.Liste_Organismes = lst;
              resolve(this.Liste_Organismes);
          } catch (e) {
            if (e.error && e.error.message) {
              this._SnackbarService.openSnackBar(e.error.message);
            }
            reject(e);
          }
          });
      }else{
        this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
        return Promise.reject(new Error("Aucun lecteur connecté."));
      }
    }else{ // on a deja fait appel a getListRegime rappel non necessaire.
      return this.Liste_Organismes;
    }
  }

  public async getInfosPS(idJfse:number): Promise<FseIo>{
    const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
    const body = {
        idJfse: idJfse,
        tokenIdJfse:token,
        cardReader: "",
        OS:""
    };
    return new Promise<FseIo>(async (resolve, reject) => {
      try {
        const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/getInfoPS`, body, this.httpOptions).toPromise();
        if(res.body?.newToken){
         this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
        }
        resolve(res.body);
      } catch (e) {
        if (e.error && e.error.message) {
          this._SnackbarService.openSnackBar(e.error.message);
        }
        reject(e);
      }
      });

  }



  public scanQRcode(): Promise<FseIo>{
    const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
    // const token = "qsdfghjklmkjhgfd";
    const rawIdJfse = this.getFromSession(this.currentUser.id, 'iDjfse');
    const idJfse = isNaN(Number(rawIdJfse)) ? 0 : Number(rawIdJfse); //securité si JFSE non numerique
    const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
    if(readerStatus){ // si pas de reader erreur snack bar
      const body = {
        idJfse: idJfse, // Obligatoire 0 si inconnu
        // tokenIdJfse: token, // Optionnel voir si present dans sessionStorage
        tokenIdJfse:token,
        // codePorteur: "", // Optionnel 4 chiffre
        cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        OS:readerStatus.operatingSystem, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
      };

       return new Promise<FseIo>(async (resolve, reject) => {
         try {
           const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/scanQRcode`, body, this.httpOptions).toPromise();
           if(res.body?.newToken){
            this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
           }
           resolve(res.body);
         } catch (e) {
           if (e.error && e.error.message) {
             this._SnackbarService.openSnackBar(e.error.message);
           }
           reject(e);
         }
         });
    }else{
      this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
    }
  }

  public async deleteContexteApCV(identifiantApCV:string | ''): Promise<FseIo> {
    const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
    // const token = "qsdfghjklmkjhgfd";
    const rawIdJfse = this.getFromSession(this.currentUser.id, 'iDjfse');
    const idJfse = isNaN(Number(rawIdJfse)) ? 0 : Number(rawIdJfse); //securité si JFSE non numerique
    const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
    if(readerStatus){ // si pas de reader erreur snack bar
      const body = {
        idJfse: idJfse, // Obligatoire 0 si inconnu
        // tokenIdJfse: token, // Optionnel voir si present dans sessionStorage
        tokenIdJfse:token,
        // codePorteur: "", // Optionnel 4 chiffre
        cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        OS:readerStatus.operatingSystem, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        identifiantApCV: identifiantApCV,
      };

       return new Promise<FseIo>(async (resolve, reject) => {
         try {
           const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/deleteContexteApCV`, body, this.httpOptions).toPromise();
           if(res.body?.newToken){
            this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
           }
           resolve(res.body);
         } catch (e) {
           if (e.error && e.error.message) {
             this._SnackbarService.openSnackBar(e.error.message);
           }
           reject(e);
         }
         });
    }else{
      this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
    }
  }

  public async restitutionContexteApCV(): Promise<FseIo> {
    const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
    // const token = "qsdfghjklmkjhgfd";
    const rawIdJfse = this.getFromSession(this.currentUser.id, 'iDjfse');
    const idJfse = isNaN(Number(rawIdJfse)) ? 0 : Number(rawIdJfse); //securité si JFSE non numerique
    const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
    if(readerStatus){ // si pas de reader erreur snack bar
      const body = {
        idJfse: idJfse, // Obligatoire 0 si inconnu
        // tokenIdJfse: token, // Optionnel voir si present dans sessionStorage
        tokenIdJfse:token,
        // codePorteur: "", // Optionnel 4 chiffre
        cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        OS:readerStatus.operatingSystem, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
      };

       return new Promise<FseIo>(async (resolve, reject) => {
         try {
           const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/restitutionContexteApCV`, body, this.httpOptions).toPromise();
           if(res.body?.newToken){
            this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
           }
           resolve(res.body);
         } catch (e) {
           if (e.error && e.error.message) {
             this._SnackbarService.openSnackBar(e.error.message);
           }
           reject(e);
         }
         });
    }else{
      this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
    }
  }

  public initTabGuiLOT(idJfse:number,idreader:string): Promise<FseIo>{ //depuis Admin uniquement

    const body = {
      idJfse: idJfse, // Obligatoire 0 si inconnu
      tokenIdJfse:'',
      cardReader: idreader, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
      OS:'admin', //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
    };

     return new Promise<FseIo>(async (resolve, reject) => {
       try {
         const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/tabGestLOT`, body, this.httpOptions).toPromise();
        //  if(res.body?.newToken){
        //   this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
        //  }
         resolve(res.body);
       } catch (e) {
         if (e.error && e.error.message) {
           this._SnackbarService.openSnackBar(e.error.message);
         }
         reject(e);
       }
      });
  }

  public initTabGestEtab(idreader:string, token:string): Promise<FseIo>{ //depuis Admin uniquement

    const body = {
      idJfse: 0, // Obligatoire 0 si inconnu
      tokenIdJfse: token,
      cardReader: idreader, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
      OS:'admin', //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
    };

     return new Promise<FseIo>(async (resolve, reject) => {
       try {
         const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/tabGestEtab`, body, this.httpOptions).toPromise();
        //  if(res.body?.newToken){
        //   this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
        //  }
         resolve(res.body);
       } catch (e) {
         if (e.error && e.error.message) {
           this._SnackbarService.openSnackBar(e.error.message);
         }
         reject(e);
       }
      });
  }

  public initTabGestUser(idreader:string, token:string): Promise<FseIo>{ //depuis Admin uniquement

    const body = {
      idJfse: 0, // Obligatoire 0 si inconnu
      tokenIdJfse: token,
      cardReader: idreader, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
      OS:'admin', //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
    };

     return new Promise<FseIo>(async (resolve, reject) => {
       try {
         const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/tabGestUser`, body, this.httpOptions).toPromise();
        //  if(res.body?.newToken){
        //   this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
        //  }
         resolve(res.body);
       } catch (e) {
         if (e.error && e.error.message) {
           this._SnackbarService.openSnackBar(e.error.message);
         }
         reject(e);
       }
      });
  }

  public initParamAdmin(token:string): Promise<FseIo>{ //depuis Admin uniquement

    const body = {
      idJfse: 0, // Obligatoire 0 si inconnu
      tokenIdJfse: token,
      cardReader: '', //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
      OS:'admin', //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
    };

     return new Promise<FseIo>(async (resolve, reject) => {
       try {
         const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/paramAdmin`, body, this.httpOptions).toPromise();
        //  if(res.body?.newToken){
        //   this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
        //  }
         resolve(res.body);
       } catch (e) {
         if (e.error && e.error.message) {
           this._SnackbarService.openSnackBar(e.error.message);
         }
         reject(e);
       }
      });
  }

  public initTLA(idJfse:number): Promise<FseIo>{
    const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
    const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
    if(readerStatus){ // si pas de reader erreur snack bar
      const body = {
        idJfse: idJfse, // Obligatoire 0 si inconnu
        tokenIdJfse:token,
        cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        OS:readerStatus.operatingSystem, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
      };

       return new Promise<FseIo>(async (resolve, reject) => {
         try {
           const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/gestionTLA`, body, this.httpOptions).toPromise();
           if(res.body?.newToken){
            this.saveToSession(this.currentUser.id, 'jFseUserToken', res.body.newToken);
           }
           resolve(res.body);
         } catch (e) {
           if (e.error && e.error.message) {
             this._SnackbarService.openSnackBar(e.error.message);
           }
           reject(e);
         }
         });
    }else{
      this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
    }
  }

  public async TLA_getListeFacturesVisite(): Promise<FseIo> {
    const token = this.getFromSession(this.currentUser.id, 'jFseUserToken');
    const rawIdJfse = this.getFromSession(this.currentUser.id, 'iDjfse');
    const idJfse = isNaN(Number(rawIdJfse)) ? 0 : Number(rawIdJfse); //securité si JFSE non numerique
    const readerStatus = this.getReaderStatusFromSession(this.currentUser.id);
    if(readerStatus){ // si pas de reader erreur snack bar
      const body = {
        idJfse: idJfse, // Obligatoire 0 si inconnu
        // tokenIdJfse: token, // Optionnel voir si present dans sessionStorage
        tokenIdJfse:token,
        // codePorteur: "", // Optionnel 4 chiffre
        cardReader: readerStatus.id, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
        OS:readerStatus.operatingSystem, //recup sur appel localhost:8888/lecteurs/id '{{CardReaderId}}',
      };

      return new Promise<FseIo>(async (resolve, reject) => {
        try {
          const res = await this.http.post<FseIo>(`${environment.basePath}/fse/praticien/gestionTLA/getListeFacturesVisite`, body, this.httpOptions).toPromise();
          resolve(res.body);
        } catch (e) {
          if (e.error && e.error.message) {
            this._SnackbarService.openSnackBar(e.error.message);
          }
          reject(e);
        }
        });
        // Retourne uniquement le corps de la réponse
    }else{
      this._SnackbarService.openSnackBar('Aucun lecteur connecté, si l\'erreur persiste contacter le SAV...');
    }

  }

}
